import type { } from "i18next";
import * as i18next from "i18next";
import { initReactI18next } from "react-i18next";
import { I18N_RESOURCES } from "../../i18n";


export type ReferenceNamespace = "constant" | "builder";

export type TranslationServiceConfig = {
	builders: Readonly<Record<string, string>>;
	constants: Readonly<Record<string, string>>;
};
export class TranslationService {
	static readonly #LANG = "en";

	#i18n!: i18next.i18n;

	/**
	 * Called by `Services`.
	 */
	private async initialise(config: Readonly<TranslationServiceConfig>) {
		this.#i18n = i18next.createInstance({
			lng: TranslationService.#LANG,
			debug: process.env["NODE_ENV"] === "develop",
			resources: {
				[TranslationService.#LANG]: {
					...I18N_RESOURCES,
					builder: config.builders,
					constant: config.constants,
				},
			},
			interpolation: {
				escapeValue: false,
			},
			react: {
				transSupportBasicHtmlNodes: true,
			},
		});

		await this.i18n
			.use(initReactI18next)
			.init();
	}

	get i18n(): i18next.i18n {
		return this.#i18n;
	}

	get translate(): i18next.TFunction<"common"> {
		return this.#i18n.t;
	}

	constant(key: string): string {
		return this.translate("constant:" + key);
	}

	resolveValueOrI18nKey<T, Fallback>(value: T | undefined, key: string | undefined, values: Record<string, unknown>| undefined, fallback?: Fallback): T | string | Fallback;
	resolveValueOrI18nKey<T>(value: T | undefined, key: string | undefined, values: Record<string, unknown> | undefined): T | string;
	resolveValueOrI18nKey<T, Fallback>(value: T, key: string | undefined, values: Record<string, unknown> | undefined, fallback?: Fallback): T | string | Fallback {
		if(value) {
			return value;
		}
		if(key) {
			return this.translate(key, values ?? {}) as string;
		}

		return fallback ?? "";
	}

	async addNamespace(namespace: string, resource: Promise<Readonly<Record<string, unknown>>>): Promise<void> {
		this.#i18n.addResourceBundle(TranslationService.#LANG, namespace, await resource);
	}

	reverseTranslate(namespace: ReferenceNamespace, translated: string): string | null {
		const ns = this.#i18n.getResourceBundle(TranslationService.#LANG, namespace);
		const key = Object.entries(ns)
			.find(([ _, value ]) => value === translated)
			?.[0] ?? null;

		return key;
	}
}
